import type { InsuranceFragment } from 'api/core/generated';
import {
  AppRoutePaths,
  AppSearchParams,
  IncomeInsuranceRoutePaths,
} from 'app-constants';
import {
  DEFAULT_INSURANCE_PROVIDER_TYPE,
  DEFAULT_MAX_INSURANCE_PERIOD,
} from 'app-constants/insurance.constants';
import { useRootContext } from 'context/root';
import { useGetInsuranceByHashLazy } from 'hooks/use-get-insurance-by-hash-lazy';
import { useGetInsuranceProviderOptionsSuspense } from 'hooks/use-get-insurance-provider-options-suspense';
import { useStoreInsurance } from 'hooks/use-store-insurance';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-use';
import { replaceOrAddQueryParamsInUrl } from 'services';

type RootInsuranceStore = {
  storedInsuranceGlobal: Nullish<InsuranceFragment>;
  isStoringInsurance: boolean;
};

export const rootInsuranceStore: RootInsuranceStore = {
  storedInsuranceGlobal: null,
  isStoringInsurance: false,
};

export const useRootLogic = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user } = useRootContext();
  const isRootPath = pathname?.split('/').length === 2;
  const [searchParams] = useSearchParams();
  const hash = searchParams.get(AppSearchParams.insuranceHash);

  const {
    storeInsurance,
    storedInsurance,
    isStoringInsurance,
    resetStoredInsurance,
  } = useStoreInsurance();

  const { getInsurance, insurance, resetInsurance } =
    useGetInsuranceByHashLazy();

  const { insuranceProviderOptions } = useGetInsuranceProviderOptionsSuspense({
    variables: {
      provider_type: DEFAULT_INSURANCE_PROVIDER_TYPE,
      insurance_period: DEFAULT_MAX_INSURANCE_PERIOD,
    },
  });

  const storeInsuranceAndReturnHash = useCallback(async () => {
    try {
      if (!user?.id) {
        throw new Error('User ID is missing when storing insurance');
      }

      const defaultInsuranceProviderOption = insuranceProviderOptions?.[0];

      if (!defaultInsuranceProviderOption) {
        throw new Error(
          'Default insurance provider option is missing when redirecting with updated insurance hash',
        );
      }

      const {
        provider_type,
        insurance_period,
        insured_amount,
        premium_amount,
        partner_premium_amount,
      } = defaultInsuranceProviderOption;

      let insuranceData: {
        data: { insurance: InsuranceFragment | undefined | null };
      };

      if (storedInsurance) {
        insuranceData = { data: { insurance: storedInsurance } };
      } else if (insurance) {
        insuranceData = { data: { insurance: insurance } };
      } else if (hash) {
        const result = await getInsurance();
        insuranceData = { data: { insurance: result.data?.insurance } };
      } else {
        const result = await storeInsurance({
          variables: {
            provider_type: provider_type || DEFAULT_INSURANCE_PROVIDER_TYPE,
            insurance_period,
            insured_amount,
            premium_amount,
            partner_premium_amount,
            user_id: user.id,
          },
        });
        insuranceData = { data: { insurance: result.data?.insurance } };
      }

      rootInsuranceStore.storedInsuranceGlobal = insuranceData.data?.insurance;

      const insuranceHash = insuranceData.data?.insurance?.hash;

      if (!insuranceHash) {
        throw new Error('Insurance hash is missing after storing insurance');
      }

      return insuranceHash;
    } catch (error) {
      console.error(error);
      throw new Error('Error storing insurance');
    }
  }, [
    insuranceProviderOptions,
    storeInsurance,
    user?.id,
    storedInsurance,
    getInsurance,
    hash,
    insurance,
  ]);

  const storeAndRedirectWithUpdatedInsuranceHash = async () => {
    const insuranceHash = await storeInsuranceAndReturnHash();
    if (!insuranceHash) {
      throw new Error(
        'Insurance hash is missing when redirecting with updated insurance hash',
      );
    }

    const path = isRootPath
      ? `/${AppRoutePaths.INCOME_INSURANCE}/${IncomeInsuranceRoutePaths.SIGNING}`
      : pathname;

    const customCurrentPageUrl = replaceOrAddQueryParamsInUrl({
      url: `${path}${search}`,
      params: {
        [AppSearchParams.insuranceHash]: insuranceHash,
      },
    });

    window.history.pushState({}, '', customCurrentPageUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));

    navigate(customCurrentPageUrl);
  };

  const resetInsuranceInstance = () => {
    resetStoredInsurance();
    resetInsurance();
    rootInsuranceStore.storedInsuranceGlobal = null;
    rootInsuranceStore.isStoringInsurance = false;
  };

  return {
    storeInsurance,
    storedInsurance:
      rootInsuranceStore.storedInsuranceGlobal || storedInsurance,
    isStoringInsurance,
    insuranceProviderOptions,
    storeAndRedirectWithUpdatedInsuranceHash,
    resetInsuranceInstance,
  };
};
